import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { startAppListening } from '../middlewares/listener.middleware';
import {
  ILoanOffer,
  ILoanOfferApplicant,
  ILoanOfferForm,
  ILoanOfferState,
} from '~/interfaces/loanOffer';
import { ClientType, SignerType } from '~/enums';
import { LoanType } from '@qred/shared-enums';
import {
  IBeneficialOwner,
  IKYCQuestionnaireAnswer,
  ISigner,
} from '~/interfaces/shared';
import { syncOfferPersons } from '~/helpers/shared.helper';
import { applyKYCAnswer } from '~/helpers/kycQuestionnaire.helper';
import { ApiStatus } from '../types/sharedTypes';

export const loanOfferInitialState = Object.freeze<ILoanOfferState>({
  currentStep: 1,
  stepStartTimestamp: new Date().toString(),
  clientType: ClientType.Unknown,
  hasWebIdIdentified: false,
  overview: {
    minPayoutAmount: 0,
    defaultPayoutAmount: 0,
    amountCannotBeChanged: false,
    defaultTerm: 0,
    maxPayoutAmount: 0,
    maxTerm: 0,
    minTerm: 0,
    currentLoanAmountLeft: 0,
    loanType: LoanType.FLEX_LOAN,
    answeredDate: '',
    applicationId: 0,
    applicationUuid: '',
    accountNumber: '',
    bankClearing: '',
    bankAccounts: [],
    campaignDiscount: 0,
    campaignInterestRateDiscount: 0,
    campaignPrincipalFreeMonths: 0,
    campaignType: null,
    clientId: 0,
    companyNumber: '',
    companyType: '',
    email: '',
    interestPercent: 0,
    landingUrl: '',
    language: '',
    lenderName: '',
    lenderRep: '',
    phone: '',
    loanPurpose: '',
    loanPurposeManual: '',
    owners: [],
    personalId: '',
    reason: '',
    rPartnerId: 0,
    signers: [],
    granter: '',
  },
  fetchedBeneficialOwners: [],
  apiStatus: {
    offer: ApiStatus.Idle,
    post: ApiStatus.Idle,
    fetchKycQuestions: ApiStatus.Idle,
    postKycAnswers: ApiStatus.Idle,
    fetchBeneficialOwners: ApiStatus.Idle,
    postIdentify: ApiStatus.Idle,
  },
  form: {
    account: {},
    openBankingAccount: null,
    applicationId: 0,
    payoutAmount: 0,
    clientId: 0,
    guarantors: [],
    invoiceEmail: '',
    clientName: '',
    isPolicyAgreed: true,
    accountNumber: null,
    kycQuestionnaireQuestions: [],
    kycQuestionnaireAnswers: [],
    loanPeriod: 0,
    loanPurposeManual: '',
    offerIsAgreed: false,
    owners: [],
    ownershipFiles: [],
    personalLiabilityIsAgreed: false,
    personalNumber: '',
    purposeOfLoan: '',
    regularMailForInvoices: false,
    signers: [],
    applicant: {
      fullName: '',
      personalNumber: '',
      percentage: '',
      id: '',
    },
    isDirectDebitEnabled: false,
  },
});

type IUpdatableFormFields = Omit<ILoanOfferForm, 'owners'>;

const loanOfferSlice = createSlice({
  name: 'loanOffer',
  initialState: loanOfferInitialState,
  reducers: {
    goToNextStep: (state) => {
      state.currentStep += 1;
    },
    goToPreviousStep: (state) => {
      state.currentStep -= 1;
    },
    updateForm: (
      state,
      action: PayloadAction<Partial<IUpdatableFormFields>>
    ) => {
      state.form = { ...state.form, ...action.payload };
    },
    updateOverview: (state, action: PayloadAction<ILoanOffer>) => {
      state.overview = { ...state.overview, ...action.payload };
    },
    setApiStatus: (
      state,
      action: PayloadAction<Partial<ILoanOfferState['apiStatus']>>
    ) => {
      state.apiStatus = {
        ...state.apiStatus,
        ...action.payload,
      };
    },
    setStepStartTimestamp: (state) => {
      state.stepStartTimestamp = new Date().toString();
    },
    setFetchedBeneficialOwners: (
      state,
      action: PayloadAction<IBeneficialOwner[]>
    ) => {
      state.fetchedBeneficialOwners = action.payload;
    },
    updateApplicant: (
      state,
      action: PayloadAction<Partial<ILoanOfferApplicant>>
    ) => {
      state.form.applicant = {
        ...state.form.applicant,
        ...action.payload,
      };
    },
    updateAdditionalOwner: (state, action: PayloadAction<IBeneficialOwner>) => {
      const updatedOwners = state.form.owners.map((o) => {
        if (o.id === action.payload.id) {
          return action.payload;
        }
        return o;
      });

      state.form.owners = updatedOwners;
    },
    addAdditionalOwner: (state, action: PayloadAction<IBeneficialOwner>) => {
      state.form.owners.push(action.payload);
    },
    setBeneficialOwners: (state, action: PayloadAction<IBeneficialOwner[]>) => {
      state.form.owners = action.payload;
    },
    setHasWebIdIdentified: (state, action: PayloadAction<boolean>) => {
      state.hasWebIdIdentified = action.payload;
    },
    removeAdditionalOwnerById: (state, action: PayloadAction<string>) => {
      state.form.owners = state.form.owners.filter(
        (o) => o.id !== action.payload
      );
    },
    updateKYCAnswers: (
      state,
      action: PayloadAction<IKYCQuestionnaireAnswer>
    ) => {
      state.form.kycQuestionnaireAnswers = applyKYCAnswer({
        answers: state.form.kycQuestionnaireAnswers,
        questions: state.form.kycQuestionnaireQuestions,
        newAnswer: action.payload,
      });
    },
    updateSigner: (state, action: PayloadAction<ISigner>) => {
      const { form } = state;
      const signerKeys: Record<
        SignerType,
        keyof Pick<ILoanOfferForm, 'signers' | 'guarantors'> | ''
      > = {
        [SignerType.Signer]: 'signers',
        [SignerType.Guarantor]: 'guarantors',
        [SignerType.Applicant]: '',
      };

      const reduxKey = signerKeys[action.payload.type];
      if (reduxKey) {
        const updatedList = form[reduxKey].map((s) => {
          if (s.signerId === action.payload.signerId) {
            return {
              ...s,
              ...action.payload,
            };
          }
          return s;
        });
        state.form[reduxKey] = updatedList;
      }
    },
  },
});

export const {
  goToNextStep,
  goToPreviousStep,
  updateForm,
  updateOverview,
  setApiStatus,
  setFetchedBeneficialOwners,
  updateApplicant,
  updateAdditionalOwner,
  addAdditionalOwner,
  removeAdditionalOwnerById,
  updateKYCAnswers,
  updateSigner,
  setBeneficialOwners,
  setHasWebIdIdentified,
} = loanOfferSlice.actions;

export type TLoanOfferSliceActions = typeof loanOfferSlice.actions;

export default loanOfferSlice.reducer;

startAppListening({
  matcher: isAnyOf(goToNextStep, goToPreviousStep),
  effect: async (_action, listenerApi) => {
    listenerApi.dispatch(loanOfferSlice.actions.setStepStartTimestamp());
  },
});

// if signers, owners or the applicant is updated, we need to check if the same person is in the
// other lists and update it there as well
startAppListening({
  matcher: isAnyOf(
    addAdditionalOwner,
    updateAdditionalOwner,
    updateSigner,
    updateApplicant,
    setBeneficialOwners,
    removeAdditionalOwnerById
  ),
  effect: (action, listenerApi) => {
    syncOfferPersons(
      action,
      listenerApi.getState().loanOffer,
      listenerApi.getOriginalState().loanOffer,
      listenerApi.dispatch,
      loanOfferSlice.actions
    );
  },
});
